/*
 * File: quick-helper.ts
 * File Created: 2024-02-29 04:12:27
 * Author: Val Liu <valuis0429@gmail.com>
 *
 * -----
 * Last Modified: 2025-12-10 13:42:54
 * Modified By: Val Liu <valuis0429@gmail.com>
 * -----
 */

import { IBaseComponentUITag } from "@root/components/interfaces";
import { MetaParser } from "@root/core/server/parser/meta-parser";
import { ObjectHelper } from "./object-helper";
import { StringHelper } from "./string-helper";
import { IPermissionCheck, IRouterAuth } from "./permission-helper";
import { Registry } from "./registry";

/// IQuick registration
interface IUIQuickDefaultMeta {}
const { registerClass: set, getInstance: get } = Registry.create<IUIQuickDefaultMeta>(
    Symbol("UIQuickDefault"),
);

export const UIQuick = {
    /**
     * Registers a class with the UIQuick registry.
     */
    set,
    /**
     * Retrieves a class from the UIQuick registry.
     */
    get,
    /**
     * Retrieves an instance of a registered class by name.
     * @param name - The name of the class to retrieve.
     * @returns An instance of the requested class.
     */
    getInstance: (name: string | TemplateStringsArray) => {
        name = typeof name === "string" ? name : name.join("");
        return get(name)?.instance;
    },
    /**
     * Returns a map of all registered IQuickUnit instances by class name.
     * @returns A map of class names to IQuickUnit instances.
     */
    getInstanceMap: (): { [key: string]: IQuickUnit } => {
        return (
            get()?.reduce((final, value) => {
                const name = value.ctor.name;
                final[name] = UIQuick.getInstance(name);
                return final;
            }, {} as any) ?? {}
        );
    },
};

/// buildHierachies config
interface IBuildHierachiesConfig {
    mode: /// return hierarchies that contain all the required quicks,
    /// but it's okay if there are extra parts beyond that.
    | "contain"
        /// return hierarchies that exactly match all the required quicks,
        /// without including any extra parts.
        | "equal";
}
const defaultBuildHierachies: IBuildHierachiesConfig = {
    mode: "contain",
};

/**
 * Interface for defining a detailed information structure of an IQuick.
 */
export interface IQuickUnitSubTree {
    /// the child property name of this quick.
    /// by default, it's singlized for parent, and pluralized for children.
    propertyName?: string;

    /// if true, it won't show up on the detail tabs.
    hidden?: boolean | ((this: IQuickUnit) => boolean);

    /// if true, it's a single child, not children.
    single?: boolean | ((this: IQuickUnit) => boolean);

    /// the quick instance.
    quick: IQuickUnit;
}

/**
 * Abstract class representing a basic quick unit.
 */
export abstract class IQuickUnit {
    /// the title of this element. ex: 資產大樓
    abstract title: string;
    /// the api path of this element. ex: /property-group
    /// if clientPath not specify, it's also the client path.
    abstract path: string;
    /// provide value to be simulate mode.
    value?: any;
    /// specify this only if the client path is different with the api path.
    clientPath?: string;
    /// the represent icon of this element.
    icon?: string;

    /// hireachy
    abstract parents: { (): IQuickUnitAny[] } | null;
    abstract children: { (): IQuickUnitAny[] } | null;

    /// files: default false.
    file?: boolean | string | string[];

    /// the params feed into table / form
    // global where params of this quick
    paramsWhere?: (this: IQuickUnit) => any;
    // params for specific request types
    paramsRead?: (this: IQuickUnit) => any;
    paramsUpdate?: (this: IQuickUnit) => any;
    // placeholder: not in use for now.
    // wait for it's actually being needed.
    paramsDetail?: (this: IQuickUnit) => any;
    paramsCreate?: (this: IQuickUnit) => any;
    paramsDelete?: (this: IQuickUnit) => any;

    /// additional props feed into table or form
    propsRead?: (this: IQuickUnit) => any;
    propsUpdate?: (this: IQuickUnit) => any;
    propsDetail?: (this: IQuickUnit) => any;
    propsCreate?: (this: IQuickUnit) => any;
    propsDelete?: (this: IQuickUnit) => any;

    /// the toolbox feed into table / form
    toolboxRead?: string;

    /// the uiTag feed into table / form
    uiTagCreate?: (this: IQuickUnit) => IBaseComponentUITag;
    uiTagUpdate?: (this: IQuickUnit) => IBaseComponentUITag;
    // placeholder: not in use for now.
    // wait for it's actually being needed.
    uiTagRead?: (this: IQuickUnit) => IBaseComponentUITag;
    uiTagDetail?: (this: IQuickUnit) => IBaseComponentUITag;
    uiTagDelete?: (this: IQuickUnit) => IBaseComponentUITag;

    /// interfaces
    infDetail?: string;
    infRead?: string;
    infCreate?: string;
    infUpdate?: string;
    infDelete?: string;

    /// permissions
    // allow the role(s)
    allowRead?: IPermissionCheck;
    allowCreate?: IPermissionCheck;
    allowUpdate?: IPermissionCheck;
    allowDelete?: IPermissionCheck;

    // deny the role(s)
    denyRead?: IPermissionCheck;
    denyCreate?: IPermissionCheck;
    denyUpdate?: IPermissionCheck;
    denyDelete?: IPermissionCheck;
}

/**
 * Generalized subtree structure for a quick unit.
 */
export type IQuickUnitGeneralizedSubTree = Required<IQuickUnitSubTree>;
export type IQuickSingleHierachyUnit = IQuickUnitGeneralizedSubTree;
export type IQuickHierachySummary = {
    front: IQuickSingleHierachyUnit;
    back: IQuickSingleHierachyUnit;
    child?: IQuickSingleHierachyUnit;
};

/// null for load error
export type IQuickUnitAny = IQuickUnit | IQuickUnitSubTree | null;

export class QuickHelper {
    /**
     * Maximum paging settings.
     */
    static MAX_PAGING = {
        paging: {
            page: 1,
            pageSize: Number.MAX_SAFE_INTEGER,
        },
    };

    /**
     * Resolve a quick instance by API uri.
     * Matches exact path or longest prefix (e.g., /promotion-code/redeem -> /promotion-code).
     */
    static resolveQuickByUri(uri: string): IQuickUnit | null {
        if (!uri) return null;
        const base = String(uri).split("?")[0].trim();
        const normalized = (base.startsWith("/") ? base : `/${base}`).toLowerCase();
        const quickMap = UIQuick.getInstanceMap();
        let matched: IQuickUnit | null = null;
        let matchedLen = -1;

        for (const quick of Object.values(quickMap)) {
            if (!quick) continue;
            const path = (quick.clientPath ?? quick.path ?? "").trim();
            if (!path) continue;
            const normalizedPath = (path.startsWith("/") ? path : `/${path}`).toLowerCase();
            if (
                normalized === normalizedPath ||
                normalized.startsWith(`${normalizedPath}/`)
            ) {
                if (normalizedPath.length > matchedLen) {
                    matched = quick;
                    matchedLen = normalizedPath.length;
                }
            }
        }

        return matched;
    }

    /**
     * Resolve @uiLabel i18n key for an attribute via API uri + quick interface.
     * Returns the raw i18n key string (e.g., w_LedgerNameId) or null if not found.
     */
    static resolveUILabelKeyByUri(
        uri: string,
        attrName: string,
        options?: { method?: "C" | "R" | "U" | "D" | "detail" },
    ): string | null {
        const quick = this.resolveQuickByUri(uri);
        if (!quick || !attrName) return null;

        const method = options?.method;
        const inf =
            method === "C"
                ? quick.infCreate
                : method === "U"
                  ? quick.infUpdate
                  : method === "D"
                    ? quick.infDelete
                    : method === "detail"
                      ? quick.infDetail
                      : quick.infRead ?? quick.infCreate ?? quick.infUpdate ?? quick.infDelete;

        if (!inf) return null;

        const metas = new MetaParser(inf);
        const key = String(attrName).toLowerCase();
        const meta =
            metas.attributeMap?.[attrName] ??
            metas.attributeMap?.[key] ??
            Object.values(metas.attributeMap ?? {}).find(
                (value) => value?.attr?.name?.toLowerCase?.() === key,
            );
        const label = meta?.attr?.tagMap?.uiLabel ?? null;
        return label || null;
    }

    /// the quicks, at least need one for the deepest children.
    /// all the rest is its related parents.
    static buildHierachies(
        quicks: IQuickUnitAny[],
        config: IBuildHierachiesConfig = defaultBuildHierachies,
    ): Array<IQuickHierachySummary> {
        config = ObjectHelper.deepMerge(defaultBuildHierachies, config);
        const { mode } = config;

        let summary: IQuickHierachySummary[] = [];
        if (quicks.length === 0) return summary;

        const makeClone = (
            source: IQuickUnitGeneralizedSubTree,
        ): IQuickSingleHierachyUnit => {
            const cloneQuick = { ...source.quick, parents: null, children: null };
            const clone = { ...source, quick: cloneQuick };
            return clone as IQuickSingleHierachyUnit;
        };

        const makeSummary = (front: IQuickSingleHierachyUnit): IQuickHierachySummary => {
            // Find the back by traversing from front
            front = cloneHierarchy(front);
            let back;
            let currentBack: any = (back = front);
            while ((currentBack = currentBack.quick.children?.()?.[0])) {
                back = currentBack;
            }

            return {
                front,
                back,
            };
        };

        const buildSingleHierachyFromBottom = (
            quicks: IQuickUnitGeneralizedSubTree[],
            summary: IQuickHierachySummary[],
            quick: IQuickUnitGeneralizedSubTree,
            front?: IQuickSingleHierachyUnit,
        ) => {
            if (!front) {
                front = makeClone(quick);
            } else {
                const oriFront = front;
                front = makeClone(quick);

                // Set up the parent-child relationship
                front.quick.children = () => [oriFront];
                oriFront.quick.parents = () => [front as any];
            }

            /// remove the "front" from "quicks"
            const lastQuick = quicks[quicks.length - 1];
            if (lastQuick && QuickHelper.equals(lastQuick, front)) {
                quicks.pop();
            }

            const parents = quick.quick.parents?.();

            // if (!parents || parents.length === 0) {
            //     // validate quicks to match the hierachy
            //     let cloneQuicks = [...quicks];
            //     let current: IQuickUnitGeneralizedSubTree | undefined = front;
            //     do {
            //         if (this.equals(cloneQuicks[0], current)) {
            //             cloneQuicks.shift();
            //         }
            //     } while ((current = current.quick.children?.()?.[0] as any));
            //     if (cloneQuicks.length !== 0) return;

            //     // Find the back by traversing from front
            //     front = cloneHierarchy(front);
            //     let back;
            //     let currentBack: any = (back = front);
            //     while ((currentBack = currentBack.quick.children?.()?.[0])) {
            //         back = currentBack;
            //     }

            //     summary.push({
            //         front,
            //         back,
            //     });
            //     return;
            // }

            if (mode === "contain") {
                if (!parents || parents.length === 0) {
                    summary.push(makeSummary(front));
                    return;
                }
            } else if (mode === "equal") {
                /// already no parents, but quicks not match.
                if ((!parents || parents.length === 0) && quicks.length !== 0) return;
                if (quicks.length === 0) {
                    summary.push(makeSummary(front));
                    return;
                }
            } else {
                console.error(`not handling mode: ${mode}`);
                return;
            }

            // For each parent, clone 'front' to prevent shared references
            for (const parentQuick of parents!) {
                const generalizedParent = this.generalize(parentQuick, {
                    force: true,
                    plural: false,
                    capitalize: true,
                });
                buildSingleHierachyFromBottom(quicks, summary, generalizedParent, front);
            }
        };

        // Helper function to clone the entire hierarchy from 'front'
        const cloneHierarchy = (
            hierarchy: IQuickSingleHierachyUnit,
        ): IQuickSingleHierachyUnit => {
            const clonedQuick = { ...hierarchy.quick };
            const clonedHierarchy: IQuickSingleHierachyUnit = {
                ...hierarchy,
                quick: clonedQuick,
            };

            // Recursively clone children
            if (hierarchy.quick.children) {
                const children = hierarchy.quick.children();
                if (children && children.length > 0) {
                    const clonedChildren = children.map((child) =>
                        cloneHierarchy(child as any),
                    );
                    clonedQuick.children = () => clonedChildren;
                    clonedChildren.forEach((child) => {
                        child.quick.parents = () => [clonedHierarchy];
                    });
                }
            }

            return clonedHierarchy;
        };

        // Start building hierarchies
        do {
            // Generalize the IQuickUnitAny into IQuickUnitSubTree
            let transformedQuicks = quicks.map((quick) =>
                this.generalize(quick, { force: true, plural: false, capitalize: true }),
            );
            const quick = transformedQuicks[transformedQuicks.length - 1];
            buildSingleHierachyFromBottom(transformedQuicks, summary, quick);
        } while (0);

        return summary;
    }

    /**
     * Prints the property names of each level in the hierarchy to the console, starting from both the front and back ends.
     *
     * This method traverses the hierarchy from the front quick unit to the back via its children,
     * and from the back quick unit to the front via its parents, outputting the property names at each level.
     * This is useful for debugging or visualizing the structure of the hierarchy.
     *
     * @param hierachy - The hierarchy summary containing the front and back quick units to be printed.
     */
    static printHierachy(hierachy: IQuickHierachySummary) {
        /// from front
        console.log("=== from front ===");
        for (
            let current: IQuickUnitGeneralizedSubTree | undefined = hierachy.front, i = 1;
            current != undefined;
            i++
        ) {
            console.log(`<level ${i}>`, current.propertyName);
            current = current.quick.children?.()?.[0] as any;
        }
        console.log("=== front  end ===");

        /// from back
        console.log("=== from back ===");
        for (
            let current: IQuickUnitGeneralizedSubTree | undefined = hierachy.back, i = 1;
            current != undefined;
            i++
        ) {
            console.log(`<level ${i}>`, current.propertyName);
            current = current.quick.parents?.()?.[0] as any;
        }
        console.log("=== back  end ===");
    }

    /**
     * deprecated. should use buildHierachies.
     * Builds a single hierarchy from an array of quicks.
     *
     * @param quicks - An array of quick units representing the hierarchy.
     * @returns A single hierarchy unit or null if the input array is empty.
     */
    static buildSingleHierachy(
        ...quicks: IQuickUnitAny[]
    ): IQuickSingleHierachyUnit | null {
        /// the `quicks` is an array that show the hierachy from parent to child,
        /// which should provide suficient data to build a single hierachy.
        /// for example, the quicks[0] is the root of the hierachy,
        /// the quicks[len-1] is the last child of the hierachy,
        /// which quicks[0].chilren() or its children may contain the quicks[1],
        /// and the propertyName represent the key for the quicks[0] to access quicks[1].
        /// scan all the children until match quicks[len-1], return the result array.

        if (quicks.length === 0) return null;
        let transformedQuick = quicks.map((quick) => this.generalize(quick));
        let result: IQuickSingleHierachyUnit | null = null;
        let currentResult: IQuickSingleHierachyUnit | null = null;

        // Iterate through the quicks array to build the hierachy
        for (let i = 0; i < transformedQuick.length; ++i) {
            let currentQuick = transformedQuick[i];
            let nextQuick = transformedQuick[i + 1];

            const makeClone = (source: IQuickUnitGeneralizedSubTree) => {
                const clone = ObjectHelper.deepClone(source);
                return {
                    ...clone,
                    quick: {
                        ...clone.quick,
                        parents: null,
                        children: null,
                    },
                } as unknown as IQuickSingleHierachyUnit;
            };

            if (result == null) currentResult = result = makeClone(currentQuick);
            if (nextQuick == null) return result;

            /// find nextQuick in currentQuick's children
            const child = this.find(nextQuick, this.getChildren(currentQuick));
            if (child == null) {
                console.error(
                    "<QuickHelper.buildSingleHierachy> child not found",
                    currentQuick,
                    nextQuick,
                );
                return result;
            }

            const parent: IQuickUnitGeneralizedSubTree = this.generalize(
                {
                    quick: currentResult?.quick as IQuickUnit,
                },
                { plural: false },
            );
            const transformedChild = makeClone(this.generalize(child));
            currentResult!.quick.children = () => {
                transformedChild.quick.parents = () => [parent as any];
                return [transformedChild];
            };
            currentResult = transformedChild;
        }

        return result;
    }

    /**
     * Builds server schema to include files.
     *
     * @param quick - The quick unit to include files from.
     * @param base - The base schema to include files into.
     * @returns The query schema including file references.
     */
    static includeFiles(quick: IQuickUnitAny, base: any = {}) {
        const exQuick = this.extract(quick);
        const file = exQuick.file;
        if (file == null || file === false) {
            return base;
        }
        /// default to "files"
        /// get the file list
        const fileList: string[] =
            typeof file === "boolean"
                ? ["files"]
                : typeof file === "string"
                  ? [file]
                  : file;

        for (let o of fileList) {
            let include = base.include ?? (base.include = {});
            include[o] = true;
        }

        return base;
    }

    /**
     * Builds server schema to include parents.
     *
     * @param quick - The quick unit to include parents from.
     * @param base - The base schema to include parents into.
     * @returns The updated schema including parent references.
     */
    static includeParents(quick: IQuickUnitAny, base: any = {}) {
        const parents = this.getParents(quick);

        base.select = null;
        if (parents != null) {
            base.include = parents.reduce((final, parent) => {
                final[QuickHelper.makeParentProp(parent.propertyName)] =
                    this.includeParents(parent);
                return final;
            }, {});
        }
        /// for parents, don't apply where.
        //base = ObjectHelper.deepMerge(base, exQuick.paramsRead);

        return base;
    }

    static makeParentProp(input: string): string {
        /// parent: unpluralize
        return StringHelper.camelize(StringHelper.unpluralize(input));
    }
    static makeChildrenProp(input: string): string {
        /// children: pluralize
        return StringHelper.camelize(StringHelper.pluralize(input));
    }

    /**
     * Builds server schema to include all children.
     * (used in "input-tree-select")
     *
     * @param quick - The quick unit to include children from.
     * @param base - The base schema to include children into.
     * @returns The query schema including child references.
     */
    static includeChildren(quick: IQuickUnitAny, base: any = {}) {
        const exQuick = this.extract(quick);
        const children = this.getChildren(quick);

        base.select = null;
        if (children != null) {
            base.include = children.reduce((final, child) => {
                final[this.makeChildrenProp(child.propertyName)] =
                    this.includeChildren(child);
                return final;
            }, {});
        }
        base = ObjectHelper.deepMerge(
            base,
            ObjectHelper.deepClone(exQuick.paramsWhere?.call(exQuick)),
        );

        return base;
    }
    /**
     * Builds server schema to include all children's count.
     *
     * @param quick - The quick unit to include children count from.
     * @param params - Additional parameters for counting.
     * @returns The query schema including child count references.
     */
    static includeChildrenCount(quick: IQuickUnitAny, params?: any) {
        let base: any = {};
        const children = this.getChildren(quick);

        base.select = null;
        if (children != null) {
            base.include = children.reduce((final, child) => {
                const result = {
                    _count: {
                        select: {
                            [child.propertyName]: params ?? true,
                        },
                    },
                };
                return ObjectHelper.deepMerge(final, result);
            }, {} as any);
        }
        return base;
    }

    /**
     * Retrieves the parent quick units.
     * (by default, it's singulized for parent)
     *
     * @param quick - The quick unit to get parents from.
     * @returns An array of generalized parent quick units or null.
     */
    static getParents(quick: IQuickUnitAny): IQuickUnitGeneralizedSubTree[] | null {
        return (
            this.extract(quick)
                .parents?.()
                ?.map<IQuickUnitGeneralizedSubTree>((quick) => {
                    return this.generalize(quick, { plural: false });
                }) ?? null
        );
    }

    /**
     * Retrieves the child quick units.
     * (by default, it's pluralized for children)
     *
     * @param quick - The quick unit to get children from.
     * @returns An array of generalized child quick units or null.
     */
    static getChildren(quick: IQuickUnitAny): IQuickUnitGeneralizedSubTree[] | null {
        return (
            this.extract(quick)
                .children?.()
                ?.map<IQuickUnitGeneralizedSubTree>((quick) => {
                    return this.generalize(quick);
                }) ?? null
        );
    }

    /**
     * Generalizes an IQuickUnitAny into IQuickUnitSubTree.
     *
     * @param quick - The quick unit to generalize.
     * @param options - Options for generalization.
     * @returns A generalized subtree.
     */
    static generalize(
        quick: IQuickUnitAny,
        options?: IQuickHelperGeneralizeConfig,
    ): IQuickUnitGeneralizedSubTree {
        if (quick == null) {
            console.error(`<QuickHelper.generalize> unexpected null.`);
            return null as any;
        }

        options = ObjectHelper.deepMerge(defaultQuickHelperGeneralizeConfig, options);

        let value = quick;
        if (!("quick" in value)) {
            value = <IQuickUnitSubTree>{
                quick,
            };
        }
        const isPlural =
            options.plural === true
                ? true
                : options.plural === false
                  ? false
                  : !value.single;
        const isCapitalize = options.capitalize;

        if (!value.propertyName || options.force) {
            value.propertyName = (isPlural ? StringHelper.pluralize : (o) => o)(
                (isCapitalize ? StringHelper.capitalize : StringHelper.camelize)(
                    value.quick.path,
                ),
            );
        }

        return value as IQuickUnitGeneralizedSubTree;
    }

    /**
     * Finds a target quick unit from an array of quick units.
     *
     * @param source - The source quick unit to find.
     * @param targets - The array of quick units to search within.
     * @returns The matching quick unit or null if not found.
     */
    static find(
        source: IQuickUnitAny | null | undefined,
        targets: IQuickUnitAny[] | null | undefined,
    ): IQuickUnitAny | null {
        if (source == undefined || targets == undefined) return null;
        for (let target of targets) {
            if (this.equals(source, target)) return target;
        }
        return null;
    }

    /**
     * Checks if two quick units have equal paths and titles.
     *
     * @param source - The first quick unit to compare.
     * @param target - The second quick unit to compare.
     * @returns True if the paths and titles are equal, false otherwise.
     */
    static equals(source: IQuickUnitAny, target: IQuickUnitAny): boolean {
        const transformedSource = this.extract(source);
        const transformedTarget = this.extract(target);
        return (
            transformedSource.path === transformedTarget.path &&
            transformedSource.title === transformedTarget.title
        );
    }

    /**
     * Extracts an `IQuickUnit` from an `IQuickUnitAny` source.
     *
     * @param source - The source object of type `IQuickUnitAny` to extract from.
     * @returns An `IQuickUnit` object extracted from the source.
     */
    static extract(source: IQuickUnitAny): IQuickUnit {
        if (source == null) {
            console.error(`<QuickHelper.extract> unexpected null.`);
            return null as any;
        }

        return "quick" in source ? source.quick : source;
    }

    /// utilities
    static autoWhereId(id) {
        return {
            where: { id },
        };
    }

    /// CRUD
    static serverC(quick, params?) {
        return {
            path: quick.path,
            method: "C",
            params: ObjectHelper.deepMerge(quick.paramsCreate?.call(quick), params),
        };
    }
    static infC(quick) {
        return quick.infCreate;
    }
    static propC(quick) {
        return quick.propsCreate?.call(quick);
    }

    static serverR(quick, id?, params?) {
        return {
            path: quick.path,
            /// paramsDetail / paramsRead may return mutable value should not be cloned.
            params: ObjectHelper.deepMerge(
                quick.paramsWhere?.call(quick),
                quick.paramsRead?.call(quick),
                id ? this.autoWhereId(id) : {},
                params,
            ),
        };
    }
    static infR(quick) {
        return quick.infRead;
    }
    static propR(quick) {
        return quick.propsRead?.call(quick);
    }

    static serverU(quick, id?, params?) {
        return {
            path: quick.path,
            method: "U",
            /// paramsUpdate may return mutable value should not be cloned.
            params: ObjectHelper.deepMerge(
                quick.paramsUpdate?.call(quick),
                id ? this.autoWhereId(id) : {},
                params,
            ),
        };
    }
    static infU(quick) {
        return quick.infUpdate;
    }
    static propU(quick) {
        return quick.propsUpdate?.call(quick);
    }

    static serverD(quick, id?, params?) {
        return {
            path: quick.path,
            method: "D",
            /// paramsDelete may return mutable value should not be cloned.
            params: ObjectHelper.deepMerge(
                quick.paramsDelete?.call(quick),
                id ? this.autoWhereId(id) : {},
                params,
            ),
        };
    }
    static infD(quick) {
        return quick.infDelete;
    }
    static propD(quick) {
        return quick.propsDelete?.call(quick);
    }

    static serverDetail(quick, id?, params?) {
        return {
            path: quick.path,
            method: "U",
            /// paramsDetail / paramsRead may return mutable value should not be cloned.
            params: ObjectHelper.deepMerge(
                quick.paramsWhere?.call(quick),
                quick.paramsDetail?.call(quick),
                id ? this.autoWhereId(id) : {},
                params,
            ),
        };
    }

    /// Summary
    static forTableC(quick, params?) {
        return {
            server: this.serverC(quick, params),
            interface: this.infC(quick),
            ...this.propC(quick),
        };
    }
    static forTableR(quick, id?, params?, options?: { enabled?: boolean }) {
        const enabled = options?.enabled !== false;
        const result: any = {
            server: this.serverR(quick, id, params),
            interface: this.infR(quick),
            ...this.propR(quick),
        };
        if (!enabled) delete result.server;
        return result;
    }
    static forTableU(quick, id?, params?) {
        return {
            server: this.serverU(quick, id, params),
            interface: this.infU(quick),
            ...this.propU(quick),
        };
    }
    static forTableD(quick, id?, params?) {
        return {
            server: this.serverD(quick, id, params),
            interface: this.infD(quick),
            ...this.propD(quick),
        };
    }
    static forTableDetail(quick, id?, params?) {
        return {
            server: this.serverDetail(quick, id, params),
            interface: this.infR(quick),
            ...this.propR(quick),
        };
    }
}

interface IQuickHelperGeneralizeConfig {
    /// force re-generate the propertyName
    force?: boolean;
    capitalize?: boolean;
    plural?: boolean;
}
const defaultQuickHelperGeneralizeConfig: IQuickHelperGeneralizeConfig = {};

/**
 * Preloads all quicks in the src directory.
 */
((r) => r.keys().forEach(r))(require.context("@root/src", true, /quick\.ts$/));
